import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Check, Trash2, Loader2, FileText, Edit, Plus, X, ChevronRight } from "lucide-react";
import api from "@/lib/api";

const inp = "w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50";

const UNITS = ["hora", "dia", "noite", "mes", "semana"];

export default function PriceTables() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/price-tables").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openTable = async (id) => {
    const { data } = await api.get(`/price-tables/${id}`);
    setSelected(data);
  };

  const pick = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Só é aceite PDF"); return; }
    if (f.size > 5_000_000) { toast.error("PDF demasiado grande (máx 5MB)"); return; }
    const r = new FileReader();
    r.onload = async () => {
      setUploading(true);
      try {
        toast.info("A extrair preços com IA... (pode demorar 20-40s)");
        await api.post("/price-tables/upload", { label: label || f.name.replace(/\.pdf$/i,''), pdf_data_url: r.result });
        toast.success("Tabela criada e extraída");
        setLabel("");
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro na extração");
      } finally { setUploading(false); }
    };
    r.readAsDataURL(f);
  };

  const activate = async (id) => {
    await api.post(`/price-tables/${id}/activate`);
    toast.success("Versão ativada");
    load();
    if (selected?.id === id) openTable(id);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar esta versão? (não é reversível)")) return;
    await api.delete(`/price-tables/${id}`);
    toast.success("Eliminada");
    setSelected(null);
    load();
  };

  const saveSelected = async () => {
    if (!selected) return;
    const { pdf_data_url, extracted_text, ...body } = selected;
    await api.put(`/price-tables/${selected.id}`, body);
    toast.success("Alterações guardadas");
    load();
  };

  const updateRow = (i, patch) => setSelected((s) => ({ ...s, rows: s.rows.map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  const addRow = () => setSelected((s) => ({ ...s, rows: [...(s.rows || []), { service_type: "", modality: "", unit: "hora", price: 0, notes: "" }] }));
  const removeRow = (i) => setSelected((s) => ({ ...s, rows: s.rows.filter((_, idx) => idx !== i) }));

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto space-y-6" data-testid="price-tables-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Administração · Motor de Preços</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Tabela de Preços</h1>
        <p className="text-muted-foreground text-sm mt-2">Faça upload de um PDF e a IA extrai automaticamente a tabela. Apenas a versão ativa é usada nas estimativas e propostas.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input data-testid="pt-label" value={label} onChange={(e)=>setLabel(e.target.value)} placeholder="Etiqueta (ex: Tabela 2026)" className={inp + " sm:w-72"} />
        <input ref={fileRef} type="file" accept="application/pdf" onChange={onFile} className="hidden" />
        <button data-testid="pt-upload" onClick={pick} disabled={uploading} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 disabled:opacity-50 text-background font-semibold rounded-md px-4 py-2 text-sm">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carregar PDF
        </button>
        <div className="text-xs text-muted-foreground sm:ml-2">PDF · máx 5MB · extração por Claude Sonnet 4.5</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <div className="bg-card border border-border rounded-2xl p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2 px-2">Versões</div>
          {list.length === 0 && <div className="text-sm text-muted-foreground/70 p-4">Sem tabelas ainda.</div>}
          {list.map((pt) => (
            <button key={pt.id} data-testid={`pt-row-${pt.id}`} onClick={()=>openTable(pt.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${selected?.id===pt.id ? "bg-muted border-[#CDC5B3]" : "border-transparent hover:bg-muted/40"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-ui text-sm">{pt.label}</span>
                {pt.active && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300"><Check className="w-3 h-3" /> Ativa</span>}
              </div>
              <div className="text-xs text-muted-foreground">{pt.version} · {new Date(pt.created_at).toLocaleDateString('pt-PT')}</div>
            </button>
          ))}
        </div>

        <div>
          {!selected && (
            <div className="bg-card border border-border rounded-2xl p-16 text-center text-muted-foreground text-sm">
              Selecione uma versão para ver e editar as linhas.
            </div>
          )}
          {selected && (
            <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{selected.version}</div>
                  <h3 className="font-display text-2xl">{selected.label}</h3>
                </div>
                <div className="flex gap-2">
                  {!selected.active && <button data-testid="pt-activate" onClick={()=>activate(selected.id)} className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/20 rounded-full px-3 py-1.5"><Check className="w-3.5 h-3.5" /> Ativar</button>}
                  <button data-testid="pt-delete" onClick={()=>remove(selected.id)} className="inline-flex items-center gap-1.5 text-xs border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-full px-3 py-1.5"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
                </div>
              </div>

              {selected.extraction_notes && (
                <div className="bg-muted/40 border border-border rounded-lg p-3 text-xs text-muted-foreground">
                  <span className="text-foreground font-semibold">Notas da IA:</span> {selected.extraction_notes}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Etiqueta</div>
                  <input data-testid="pt-label-edit" className={inp} value={selected.label} onChange={(e)=>setSelected({...selected, label:e.target.value})} />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">Caução (x meses)</div>
                  <input data-testid="pt-caucao" type="number" step="0.1" className={inp} value={selected.caucao_multiplier} onChange={(e)=>setSelected({...selected, caucao_multiplier:parseFloat(e.target.value)||1})} />
                </label>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!selected.active} disabled className="accent-primary" />
                    Ativa
                  </label>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Linhas de preço</div>
                  <button data-testid="pt-add-row" onClick={addRow} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Adicionar</button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {(selected.rows || []).map((row, i) => (
                    <div key={i} className="grid grid-cols-[1.5fr_1fr_100px_100px_1fr_28px] gap-2 items-center bg-muted/40 border border-border rounded-lg p-2.5">
                      <input data-testid={`pt-row-service-${i}`} placeholder="Serviço" className={inp} value={row.service_type||""} onChange={(e)=>updateRow(i,{service_type:e.target.value})} />
                      <input placeholder="Modalidade" className={inp} value={row.modality||""} onChange={(e)=>updateRow(i,{modality:e.target.value})} />
                      <select className={inp} value={row.unit} onChange={(e)=>updateRow(i,{unit:e.target.value})}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select>
                      <input type="number" step="0.01" placeholder="€" className={inp} value={row.price||0} onChange={(e)=>updateRow(i,{price:parseFloat(e.target.value)||0})} />
                      <input placeholder="Notas" className={inp} value={row.notes||""} onChange={(e)=>updateRow(i,{notes:e.target.value})} />
                      <button onClick={()=>removeRow(i)} className="text-muted-foreground/70 hover:text-rose-400"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button data-testid="pt-save" onClick={saveSelected} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-md px-4 py-2 text-sm">
                  <Edit className="w-4 h-4" /> Guardar alterações
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
